import { playerUsageFromContext, rootChipBudgetBreakdown } from './playerChipAccounting';
import type Context from '@/context';
import type { ChipModelDto } from '@/schemas/chip';
import { Point } from '@/models/Point';
import { rotatePointAroundOrigin } from '@/models/data/Rotation';
import { TileType } from '@/models/TileTypes';
import type { Challenge } from './definition';

export function chipFootprint(
  chip: ChipModelDto,
  placement: { x: number; y: number; rotation: number },
) {
  const origin = Point.fromData(chip.structureOriginLocationDto ?? { x: 0, y: 0 });
  const offset = new Point(placement.x, placement.y).sub(origin);
  return chip.structureCircuitDto.tileManagerDto.tiles.map((tile) => {
    const p = rotatePointAroundOrigin(
      Point.fromData(tile.globalTileLocation.location),
      origin,
      placement.rotation,
    ).add(offset);
    return { x: p.x, y: p.y };
  });
}

/** Keep definitions and artwork, but never persist simulation state in the library. */
export function cleanChipDefinition(source: ChipModelDto): ChipModelDto {
  const chip = JSON.parse(JSON.stringify(source)) as ChipModelDto;
  chip.nativeContinuation = undefined;
  for (const instance of [chip.editingInstanceDto, ...chip.chipInstanceDtos]) {
    instance.logicGraphDto = { nodes: [], edges: [] };
    instance.templateToInstanceIdMap = undefined;
  }
  for (const circuit of [chip.logicCircuitDto, chip.structureCircuitDto])
    for (const tile of circuit.tileManagerDto.tiles) {
      tile.logicNodeIds = undefined;
      if (
        tile.tileType === TileType.SingleInterface &&
        tile.state &&
        typeof tile.state === 'object' &&
        'inputMode' in tile.state &&
        tile.state.inputMode !== 'lever' &&
        'toggleState' in tile.state
      )
        tile.state.toggleState = 0;
    }
  return chip;
}

export function chipBudget(context: Context, chipId: string) {
  const challenge = context.challenge;
  if (!challenge) return undefined;
  const rule = challenge.chipLibrary?.find((entry) => entry.chip.id === chipId);
  const candidate = context.chipStore.chips.find((c) => c.id === chipId);
  const category =
    !challenge.playerChips ||
    (challenge.playerChips.permissionsVersion === 2 && !!rule) ||
    (candidate?.kind === 'bundle-adapter'
      ? challenge.playerChips.bundleAdapters
      : challenge.playerChips.chips);
  const used = context.mainChip.logicCircuit.circuitModel.chips.filter(
    (c) => c.chipId === chipId,
  ).length;
  const fixed =
    challenge.starterChips?.filter((c) => c.chipId === chipId && c.fixed).length ?? 0;
  let playerUsed = used - fixed;
  if (challenge.playerChips) {
    try {
      playerUsed = playerUsageFromContext(context).chipCounts[chipId] ?? 0;
    } catch {
      /* Invalid graph is reported by root validation. */
    }
  }
  const atRoot =
    context.editor.currentCircuit === context.mainChip.logicCircuit.circuitModel;
  let breakdown: ReturnType<typeof rootChipBudgetBreakdown> | undefined;
  if (
    challenge.playerChips &&
    atRoot &&
    rule?.max !== undefined &&
    playerUsed > rule.max
  ) {
    try {
      breakdown = rootChipBudgetBreakdown(context, chipId, 'chips');
    } catch {
      /* Root validation reports invalid graphs. */
    }
  }
  // The allowance is for player pieces. Fixed scaffold is supplied separately.
  return {
    allowed: category && (rule?.available ?? !!challenge.playerChips),
    remaining:
      rule?.max === undefined || (challenge.playerChips && !atRoot)
        ? undefined
        : rule.max - playerUsed,
    maximum: rule?.max,
    breakdown,
    used: playerUsed,
  };
}

export function fixedChipCells(challenge: Challenge) {
  return (challenge.starterChips ?? [])
    .filter((p) => p.fixed)
    .flatMap((p) => {
      const chip = challenge.chipLibrary?.find((r) => r.chip.id === p.chipId)?.chip;
      return chip ? chipFootprint(chip, p) : [];
    });
}

/** Validate before any library can hydrate native tiles or expand nested graphs. */
export function chipLibraryProblems(
  library: NonNullable<Challenge['chipLibrary']>,
  playerChips = false,
) {
  const problems: string[] = [];
  const byId = new Map(library.map((r) => [r.chip.id, r.chip]));
  if (byId.size !== library.length) problems.push('List each chip once.');
  const expanded = new Map<string, number>();
  const visiting = new Set<string>();
  const size = (id: string): number => {
    if (expanded.has(id)) return expanded.get(id)!;
    const chip = library.find((r) => r.chip.id === id)?.chip;
    if (!chip) throw Error('Include every dependency in the chip library.');
    if (visiting.has(id)) throw Error('Recursive chips cannot be used in challenges.');
    visiting.add(id);
    let count = chip.logicCircuitDto.tileManagerDto.tiles.length;
    if (count > 4096 || chip.structureCircuitDto.tileManagerDto.tiles.length > 4096)
      throw Error(`${chip.name} exceeds the 4,096 tile challenge limit.`);
    for (const child of chip.chipInstanceDtos) {
      count += size(child.chipId);
      if (count > 4096)
        throw Error(`${chip.name} expands beyond the 4,096 tile challenge limit.`);
    }
    visiting.delete(id);
    expanded.set(id, count);
    return count;
  };
  try {
    for (const { chip } of library) {
      size(chip.id);
      for (const circuit of [chip.logicCircuitDto, chip.structureCircuitDto])
        for (const tile of circuit.tileManagerDto.tiles) {
          if (
            ![
              TileType.Wire,
              TileType.Not,
              TileType.Diode,
              TileType.Delay,
              TileType.SingleInterface,
              TileType.MultiInterface,
              TileType.Structure,
              ...(playerChips ? [TileType.BundleInterface] : []),
            ].some((type) => type === tile.tileType) ||
            tile.meta.bundle ||
            tile.meta.cable
          )
            throw Error(
              `${chip.name}: this challenge runner supports logic chips made from Wire, NOT, Diode, Delay and interfaces.`,
            );
          if (tile.tileType === TileType.Delay) {
            const delay = (tile.state as { delayTimeMs?: number })?.delayTimeMs;
            if (!Number.isFinite(delay) || delay! < 0 || delay! > 60000)
              throw Error(`${chip.name}: invalid Delay duration.`);
          }
        }
    }
  } catch (e) {
    problems.push(e instanceof Error ? e.message : 'Invalid chip library.');
  }
  return problems;
}
